/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import type { TrackablePlacement } from '../placement';
import { ActivityManager } from '../../activity/manager';
import { Opportunity } from '../../auction';
import { EventDispatcher } from '../../events';
import { Logger } from '../../logger';
import type { PlacementSize } from '../../types';
import type {
  AdvertiserMetadata,
  DiscoveredUnit,
  PlacementIdConfig,
  PlacementIdContext,
  PlacementIdResult,
  PlacementIdStrategy,
} from '../../types/advertorial';
import { generateUUID } from '../../util';
import { PlacementType, Visibility } from '../placement/constants';
import { MetadataExtractor } from './extractor';
import { normaliseAdvertorialMatchUrl } from './match-url';

const logger = Logger.getInstance();

/**
 * Per-placement attribution context. Built once on the tracker from campaign
 * data and shared by reference across all placements in a discovery batch.
 *
 * `urlMap` maps advertorial-match URLs (`eurl` on MONITORING ads) to the
 * owning Ad's delivery ID. `defaultDeliveryId` is the fallback used when no
 * URL match is found, sourced from the LEARNING_DIRECT campaign's first ad.
 * When both are absent (MONITORING-only and the unit's URL doesn't match),
 * the placement is unattributable and dispatches no events.
 */
export type AdvertorialAttribution = {
  defaultDeliveryId: string | undefined;
  urlMap: Map<string, string>;
};

const ADVERTORIAL_T_VALUE = 1;

interface AdvertorialPlacementEvents {
  AdvertorialImpression: {
    opportunityId: string;
    placementId: string;
    id: string;
    deliveryId: string;
    meta?: Record<string, string>;
    t: number;
    v: Visibility;
    pt: PlacementType;
  };
  AdvertorialInteraction: {
    opportunityId: string;
    placementId: string;
    id: string;
    deliveryId: string;
    clickUrl?: string;
    t: number;
  };
  AdvertorialAccidentalClick: {
    opportunityId: string;
    placementId: string;
    id: string;
    deliveryId: string;
    awayDuration: number;
    source: 'redirect';
    t: number;
  };
  ViewableImpression: {
    impressionId: string;
    opportunityId: string;
    placementId: string;
    deliveryId: string;
    t: number;
  };
}

/**
 * Generates stable placement IDs for advertorial units using configured strategies
 */
export class UnitIdGenerator {
  private readonly config: PlacementIdConfig;

  constructor(config: PlacementIdConfig) {
    this.config = config;
  }

  /**
   * Generate a placement ID for an element
   * @param element The DOM element
   * @param index The position index of the element in discovery order
   * @param context Optional discovery context (container element and the
   *   element's index within that container) used by the 'container-id' strategy
   * @returns Placement ID result with the ID and strategy used
   */
  public generate(
    element: HTMLElement,
    index: number,
    context?: PlacementIdContext,
  ): PlacementIdResult {
    const { strategy, fallback } = this.config;

    // Try primary strategy
    const primaryResult = this.generateWithStrategy(element, index, strategy, context);
    if (primaryResult !== null) {
      return {
        id: this.applyPrefix(primaryResult),
        strategyUsed: strategy,
      };
    }

    // Try fallback strategy if available
    if (fallback) {
      const fallbackResult = this.generateWithStrategy(element, index, fallback, context);
      if (fallbackResult !== null) {
        return {
          id: this.applyPrefix(fallbackResult),
          strategyUsed: fallback,
        };
      }
    }

    // Last resort: use position strategy
    const positionResult = this.generateWithStrategy(element, index, 'position', context);
    return {
      id: this.applyPrefix(positionResult!),
      strategyUsed: 'position',
    };
  }

  /**
   * Generate ID using a specific strategy
   * @returns The generated ID or null if strategy failed
   */
  private generateWithStrategy(
    element: HTMLElement,
    index: number,
    strategy: PlacementIdStrategy,
    context?: PlacementIdContext,
  ): string | null {
    switch (strategy) {
      case 'data-attribute':
        return this.generateFromDataAttribute(element);
      case 'css-path':
        return this.generateFromCssPath(element);
      case 'position':
        return this.generateFromPosition(index);
      case 'container-id':
        return this.generateFromContainerId(context, index);
      default:
        return null;
    }
  }

  /**
   * Generate ID from the discovery container's id plus the unit's position
   * within that container, e.g. "advert_tmg_bin_1-pos-0". Keeps IDs distinct
   * and self-describing when a page hosts multiple advertorial zones.
   *
   * When the container is an iframe (a GAM friendly iframe), its own id is
   * ad-server-generated and unstable (GPT ids embed a per-page slot index), so
   * the nearest ancestor with a publisher-authored id is used instead.
   */
  private generateFromContainerId(
    context: PlacementIdContext | undefined,
    index: number,
  ): string | null {
    const container = context?.container;
    if (!container) {
      return null;
    }

    let source: HTMLElement | null = container;
    let id = '';
    let hops = 0;
    while (source && hops < 6) {
      const candidate = source.id;
      if (candidate && candidate.trim() !== '' && !/^google_ads_iframe/.test(candidate)) {
        id = candidate;
        break;
      }
      source = source.parentElement;
      hops++;
    }

    if (!id) {
      return null;
    }

    const containerIndex = context?.containerIndex ?? index;
    return `${this.sanitize(id)}-pos-${containerIndex}`;
  }

  /**
   * Generate ID from data attribute
   */
  private generateFromDataAttribute(element: HTMLElement): string | null {
    const { attribute } = this.config;
    if (!attribute) {
      return null;
    }

    const value = element.getAttribute(attribute);
    if (value && value.trim() !== '') {
      return this.sanitize(value);
    }

    return null;
  }

  /**
   * Generate ID from CSS path
   */
  private generateFromCssPath(element: HTMLElement): string | null {
    try {
      const path = this.getCssPath(element);
      return path ? this.sanitize(path) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Generate CSS path for an element
   */
  private getCssPath(element: HTMLElement): string {
    const path: string[] = [];
    let current: HTMLElement | null = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let selector = current.nodeName.toLowerCase();

      // Add ID if available
      if (current.id) {
        selector += `#${current.id}`;
        path.unshift(selector);
        break; // ID is unique, stop here
      }

      // Add classes
      if (current.className && typeof current.className === 'string') {
        const classes = current.className
          .trim()
          .split(/\s+/)
          .filter((c) => c.length > 0);
        if (classes.length > 0) {
          selector += `.${classes.join('.')}`;
        }
      }

      // Add nth-child if needed to make it unique
      const parent = current.parentElement;
      if (parent) {
        const siblings = Array.from(parent.children);
        const sameTagSiblings = siblings.filter((s) => s.nodeName === current!.nodeName);
        if (sameTagSiblings.length > 1) {
          const index = sameTagSiblings.indexOf(current) + 1;
          selector += `:nth-child(${index})`;
        }
      }

      path.unshift(selector);

      // Limit path depth to avoid extremely long selectors
      if (path.length >= 5) {
        break;
      }

      current = current.parentElement;
    }

    return path.join(' > ');
  }

  /**
   * Generate ID from position index
   */
  private generateFromPosition(index: number): string {
    return `pos-${index}`;
  }

  /**
   * Apply prefix to the generated ID
   */
  private applyPrefix(id: string): string {
    const { prefix } = this.config;
    if (prefix && prefix.trim() !== '') {
      return `${prefix}-${id}`;
    }
    return id;
  }

  /**
   * Sanitize a string to make it a valid placement ID
   */
  private sanitize(str: string): string {
    return str
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '-') // Replace invalid chars with dash
      .replace(/-+/g, '-') // Collapse multiple dashes
      .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
  }
}

/**
 * Wraps a DiscoveredUnit and manages all per-unit tracking state.
 * Implements TrackablePlacement so it can be passed directly into the analytics pipeline
 * alongside auction-managed Placement instances.
 * Ensures impressions and interactions are each recorded at most once.
 */
export class AdvertorialPlacement
  extends EventDispatcher<AdvertorialPlacementEvents>
  implements TrackablePlacement
{
  private impressionFired: boolean = false;
  private interactionFired: boolean = false;
  private opportunity: Opportunity | null = null;
  private readonly impressionId: string = generateUUID();
  private viewabilityObserver: IntersectionObserver | null = null;
  private viewabilityTimer: ReturnType<typeof setTimeout> | null = null;
  private viewableImpressionFired: boolean = false;
  // Resolved at init time once the unit's anchors are present. Undefined means
  // the placement is unattributable and must not dispatch events.
  private deliveryId: string | undefined = undefined;

  // Properties for deferred initialization (lazy-loaded content support)
  private readonly deferUntilContent: boolean;
  private contentObserver: MutationObserver | null = null;
  private initialized: boolean = false;

  constructor(
    public readonly unit: DiscoveredUnit,
    private readonly attribution: AdvertorialAttribution,
    deferUntilContent: boolean = false,
    private readonly metadataExtractor?: MetadataExtractor,
    private readonly rilinks: boolean = false,
  ) {
    super();
    this.deferUntilContent = deferUntilContent;
    // Store properties only - no initialization yet
  }

  /**
   * Bind an Opportunity to this placement.
   * Must be called before init() for impressions and interactions to be dispatched.
   */
  public setOpportunity(opportunity: Opportunity): void {
    this.opportunity = opportunity;
  }

  /**
   * Initialize tracking for this advertorial placement.
   * This begins the initialization process and will automatically dispatch
   * an AdvertorialImpression event once the placement is ready.
   *
   * Must be called after setOpportunity().
   */
  public init(): void {
    if (this.impressionFired || this.initialized) {
      return;
    }

    if (this.deferUntilContent) {
      this.startContentObserver();
      return;
    }

    const anchors = this.unit.element.querySelectorAll<HTMLAnchorElement>('a');
    this.activate(anchors);
  }

  /**
   * Run the discovery checks (delivery-ID resolution first for fail-fast,
   * then optional internal-link filtering) and dispatch the impression if all
   * pass. Used by both the immediate path and the lzyl content-detected path.
   */
  private activate(anchors: NodeListOf<HTMLAnchorElement>): void {
    if (!this.resolveDeliveryId(anchors)) {
      logger.debug(
        `Skipping advertorial ${this.unit.placementId}: no delivery ID could be resolved`,
      );
      this.initialized = true;
      return;
    }

    if (this.rilinks && !this.hasInternalLink(anchors)) {
      logger.debug(
        `Filtering advertorial ${this.unit.placementId}: all anchor links are external`,
      );
      this.initialized = true;
      return;
    }

    this.initInternal();
    this.initialized = true;
    this.dispatchImpression();
  }

  /**
   * Resolve the delivery ID for this placement by looking up every anchor's
   * normalised URL against the attribution map; falls back to the configured
   * default when no anchor matches. Anchors are resolved at activation time
   * (not in the constructor) so lzyl-deferred placements pick them up after
   * content arrives.
   */
  private resolveDeliveryId(anchors: NodeListOf<HTMLAnchorElement>): boolean {
    for (const anchor of Array.from(anchors)) {
      if (!anchor.href) {
        continue;
      }
      const normalised = normaliseAdvertorialMatchUrl(anchor.href);
      const matched = normalised ? this.attribution.urlMap.get(normalised) : undefined;
      if (matched) {
        this.deliveryId = matched;
        return true;
      }
    }
    this.deliveryId = this.attribution.defaultDeliveryId;
    return this.deliveryId !== undefined;
  }

  public get adUnitCode(): string {
    return this.unit.placementId;
  }

  public get placementType(): PlacementType {
    return PlacementType.ADVERTORIAL;
  }

  public get visibility(): Visibility {
    const rect = this.unit.element.getBoundingClientRect();

    if (rect.width === 0 && rect.height === 0) {
      return Visibility.UNKNOWN;
    }

    // Rects for elements inside a same-origin iframe (e.g. a GAM friendly
    // iframe) are relative to the iframe's viewport, not the page — translate
    // into top-window coordinates by walking up the frame chain.
    let top = rect.top;
    let left = rect.left;
    let win: Window | null = this.unit.element.ownerDocument.defaultView;
    let hops = 0;
    while (win && win !== window && hops < 5) {
      let frameElement: Element | null = null;
      try {
        frameElement = win.frameElement;
      } catch (e) {
        break; // cross-origin parent; treat coordinates as best-effort
      }
      if (!frameElement) {
        break;
      }
      const frameRect = frameElement.getBoundingClientRect();
      top += frameRect.top;
      left += frameRect.left;
      win = frameElement.ownerDocument.defaultView;
      hops++;
    }

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const isInViewport =
      top + rect.height > 0 && left + rect.width > 0 && top < viewportHeight && left < viewportWidth;

    return isInViewport ? Visibility.VISIBLE : Visibility.NOT_VISIBLE;
  }

  public get supportedSizes(): PlacementSize[] {
    const rect = this.unit.element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return [];
    }
    return [{ width: Math.round(rect.width), height: Math.round(rect.height) }];
  }

  /**
   * Common two-part TLDs that require special handling
   */
  private static readonly COMMON_2PART_TLDS = new Set([
    'co.uk',
    'com.au',
    'co.jp',
    'co.nz',
    'co.za',
    'com.br',
  ]);

  /**
   * Extract base domain from hostname (ignoring subdomains)
   * e.g., "www.example.com" -> "example.com", "blog.example.co.uk" -> "example.co.uk"
   * @param hostname The hostname to parse
   * @returns base domain
   */
  private getBaseDomain(hostname: string): string {
    const parts = hostname.split('.');

    // Handle IP addresses and localhost
    if (parts.length === 1 || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return hostname;
    }

    if (parts.length < 2) {
      return hostname;
    }

    // Get last 2 parts (e.g., "co.uk" or "example.com")
    const last2 = parts.slice(-2).join('.');

    // Check if it's a known two-part TLD
    if (AdvertorialPlacement.COMMON_2PART_TLDS.has(last2) && parts.length >= 3) {
      // Return last 3 parts (e.g., "example.co.uk")
      return parts.slice(-3).join('.');
    }

    // Return last 2 parts for standard TLDs (e.g., "example.com")
    return last2;
  }

  /**
   * Check if two hostnames share the same base domain
   * @param hostA First hostname
   * @param hostB Second hostname
   * @returns true if same base domain, false otherwise
   */
  private sameBaseDomain(hostA: string, hostB: string): boolean {
    return this.getBaseDomain(hostA) === this.getBaseDomain(hostB);
  }

  /**
   * Check if at least one anchor tag points to the same domain as the current page
   * Uses base domain comparison to ignore subdomain differences
   * @param anchors NodeList of anchor elements
   * @returns true if at least one anchor is internal, false if all are external
   */
  private hasInternalLink(anchors: NodeListOf<HTMLAnchorElement>): boolean {
    if (anchors.length === 0) {
      // No anchors found - consider this as "no internal links"
      return false;
    }

    const currentHostname = window.location.hostname.toLowerCase();

    for (const anchor of Array.from(anchors)) {
      if (!anchor.href) {
        continue; // Skip anchors without href
      }

      try {
        const anchorUrl = new URL(anchor.href);
        const anchorHostname = anchorUrl.hostname.toLowerCase();

        if (this.sameBaseDomain(currentHostname, anchorHostname)) {
          return true; // Found at least one internal link
        }
      } catch (e) {
        // Skip invalid URLs
        continue;
      }
    }

    return false; // All links are external (or invalid)
  }

  /**
   * Dispatch the AdvertorialImpression event.
   * Called automatically after initialization completes.
   * Extracts metadata right before dispatching to ensure fresh data.
   */
  private dispatchImpression(): void {
    if (this.impressionFired || !this.opportunity || this.deliveryId === undefined) {
      return;
    }

    this.impressionFired = true;

    let metadata: AdvertiserMetadata = {};
    if (this.metadataExtractor) {
      const { metadata: extractedMetadata, errors } = this.metadataExtractor.extract(
        this.unit.element,
      );
      metadata = extractedMetadata;

      if (errors.length > 0) {
        logger.debug(`Extraction errors for ${this.unit.placementId}: ${JSON.stringify(errors)}`);
      }
    }

    const meta: Record<string, string> = Object.fromEntries(
      Object.entries(metadata).filter((entry): entry is [string, string] => entry[1] !== undefined),
    );

    logger.debug(`Dispatching impression for ${this.unit.placementId}`);

    this.dispatch('AdvertorialImpression', {
      opportunityId: this.opportunity.id,
      placementId: this.unit.placementId,
      id: this.impressionId,
      deliveryId: this.deliveryId,
      meta: Object.keys(meta).length > 0 ? meta : undefined,
      t: ADVERTORIAL_T_VALUE,
      v: this.visibility,
      pt: this.placementType,
    });

    this.startViewabilityTracking();
  }

  /**
   * Start MutationObserver to detect when anchor tags are added to the unit element.
   * Used when deferUntilContent is true (lazy-loaded content support).
   */
  private startContentObserver(): void {
    if (!this.deferUntilContent || this.initialized) {
      return;
    }

    const existingAnchors = this.unit.element.querySelectorAll<HTMLAnchorElement>('a');
    if (existingAnchors.length > 0) {
      logger.debug(
        `Content already present for ${this.unit.placementId}, initializing immediately`,
      );
      this.handleContentDetected();
      return;
    }

    // Check if MutationObserver is available
    if (typeof MutationObserver === 'undefined') {
      logger.warn(
        `MutationObserver not available for ${this.unit.placementId}, cannot defer content detection`,
      );
      return;
    }

    logger.debug(`Starting content observer for ${this.unit.placementId}`);

    try {
      this.contentObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            const hasAnchors = Array.from(mutation.addedNodes).some((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as HTMLElement;
                return element.tagName === 'A' || element.querySelector('a') !== null;
              }
              return false;
            });

            if (hasAnchors) {
              logger.debug(`Anchor tags detected for ${this.unit.placementId}, initializing`);
              this.handleContentDetected();
              return;
            }
          }
        }
      });

      this.contentObserver.observe(this.unit.element, {
        childList: true,
        subtree: true,
      });
    } catch (e) {
      logger.error(
        `Failed to setup content observer for ${this.unit.placementId}: ${e instanceof Error ? e.message : String(e)}`,
      );
      this.contentObserver = null;
    }
  }

  /**
   * Called when content is detected (anchor tags found).
   * Initializes event listeners and dispatches impression.
   * Metadata will be extracted fresh when dispatchImpression() is called.
   */
  private handleContentDetected(): void {
    if (this.initialized) {
      return;
    }

    if (this.contentObserver) {
      this.contentObserver.disconnect();
      this.contentObserver = null;
    }

    const anchors = this.unit.element.querySelectorAll<HTMLAnchorElement>('a');
    this.activate(anchors);
  }

  /**
   * Attach capture-phase click listeners to all anchor tags within the unit element.
   * The first click fires an interaction event; subsequent clicks are no-ops.
   */
  private initInternal(): void {
    const anchors = this.unit.element.querySelectorAll<HTMLAnchorElement>('a');

    anchors.forEach((anchor) => {
      anchor.addEventListener(
        'click',
        (event) => {
          if (this.interactionFired || !this.opportunity || this.deliveryId === undefined) {
            return;
          }

          this.interactionFired = true;

          const anchor = event.currentTarget as HTMLAnchorElement;
          const clickUrl = anchor.href || undefined;

          this.dispatch('AdvertorialInteraction', {
            opportunityId: this.opportunity.id,
            placementId: this.unit.placementId,
            id: this.impressionId,
            deliveryId: this.deliveryId,
            clickUrl,
            t: ADVERTORIAL_T_VALUE,
          });

          this.handleAccidentalClickCandidate(event);

          this.stopViewabilityTracking();
        },
        true,
      );
    });
  }

  // Synthetic/scripted clicks (event.isTrusted === false) must not generate
  // accidental-click signals. Extracted as a seam because jsdom cannot forge a
  // trusted event, so tests override this to exercise the positive path.
  protected isTrustedEvent(event: Event): boolean {
    return event.isTrusted;
  }

  /**
   * On a genuine click, persist a pending accidental-click flag. Advertorial
   * clicks always redirect the current tab, so the page is about to unload: we
   * write the flag synchronously here and the tracker reconciles it on the next
   * page load (or bfcache restore) if the user returns quickly. Only trusted user
   * clicks are considered.
   */
  private handleAccidentalClickCandidate(event: MouseEvent): void {
    if (!this.isTrustedEvent(event) || !this.opportunity || this.deliveryId === undefined) {
      return;
    }

    // Persist synchronously before the redirect unloads the page. localStorage
    // writes are blocking, so this commits before navigation begins.
    ActivityManager.getInstance().recordPendingAccidentalClick({
      impressionId: this.impressionId,
      deliveryId: this.deliveryId,
      url: window.location.href,
      ts: Date.now(),
    });
  }

  private startViewabilityTracking(): void {
    if (this.viewabilityObserver || this.viewableImpressionFired) {
      return;
    }

    if (!this.opportunity) {
      return;
    }

    // IntersectionObserver with 50% threshold. With the implicit (viewport)
    // root this also works for units inside same-origin iframes: the browser
    // computes intersection against the top-level viewport, including the
    // iframe's own clipping.
    this.viewabilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.unit.element) {
            this.handleViewabilityChange(entry.isIntersecting && entry.intersectionRatio >= 0.5);
          }
        });
      },
      {
        threshold: 0.5,
      },
    );

    this.viewabilityObserver.observe(this.unit.element);
  }

  /**
   * Handle viewability state changes.
   * Starts timer when element becomes 50%+ visible, cancels when it drops below.
   */
  private handleViewabilityChange(isViewable: boolean): void {
    if (isViewable) {
      if (!this.viewabilityTimer && !this.viewableImpressionFired) {
        this.viewabilityTimer = setTimeout(() => {
          this.recordViewableImpression();
        }, 1000);
      }
    } else {
      if (this.viewabilityTimer) {
        clearTimeout(this.viewabilityTimer);
        this.viewabilityTimer = null;
      }
    }
  }

  /**
   * Record a viewable impression for this placement.
   * Can only fire once per placement.
   */
  private recordViewableImpression(): void {
    if (this.viewableImpressionFired) {
      return;
    }

    if (!this.opportunity || this.deliveryId === undefined) {
      return;
    }

    this.dispatch('ViewableImpression', {
      impressionId: this.impressionId,
      opportunityId: this.opportunity.id,
      placementId: this.unit.placementId,
      deliveryId: this.deliveryId,
      t: ADVERTORIAL_T_VALUE,
    });

    this.viewableImpressionFired = true;
    this.stopViewabilityTracking();
  }

  public stopViewabilityTracking(): void {
    if (this.viewabilityObserver) {
      this.viewabilityObserver.disconnect();
      this.viewabilityObserver = null;
    }

    if (this.viewabilityTimer) {
      clearTimeout(this.viewabilityTimer);
      this.viewabilityTimer = null;
    }

    if (this.contentObserver) {
      this.contentObserver.disconnect();
      this.contentObserver = null;
    }
  }

  /**
   * Cleanup all observers and timers.
   * Call this when the placement is being permanently removed.
   */
  public destroy(): void {
    this.stopViewabilityTracking();
  }
}
